import type { CurrentUser, MeResponse } from '@bigint-project/cloud-contracts';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import type { CloudApi } from './api/client';
import { ApiError, createCloudApiClient, errorMessage } from './api/client';
import { InvitationAccept } from './components/InvitationAccept';
import { LocalProjectApp } from './components/LocalProjectApp';
import { ProjectDashboard } from './components/ProjectDashboard';
import { ProjectWorkspace } from './components/ProjectWorkspace';
import { ErrorMessage, Loading } from './components/Status';
import type { EditorHostComponent } from './platform/EditorHost';
import { CloudEditorHost } from './platform/CloudEditorHost';

interface AppProps {
  readonly api?: CloudApi;
  readonly editorHost?: EditorHostComponent;
}

type Route =
  | { readonly kind: 'projects' }
  | { readonly kind: 'project'; readonly projectId: string }
  | { readonly kind: 'invitation'; readonly token: string }
  | { readonly kind: 'local' };

function parseRoute(pathname: string): Route {
  if (pathname === '/local') return { kind: 'local' };
  const project = /^\/projects\/([^/]+)$/.exec(pathname);
  if (project?.[1] !== undefined) return { kind: 'project', projectId: decodeURIComponent(project[1]) };
  const invitation = /^\/invitations\/([^/]+)$/.exec(pathname);
  if (invitation?.[1] !== undefined) return { kind: 'invitation', token: decodeURIComponent(invitation[1]) };
  return { kind: 'projects' };
}

function LoginPage({
  api,
  error,
  onLocal,
}: {
  readonly api: CloudApi;
  readonly error?: string;
  readonly onLocal: () => void;
}): ReactElement {
  return (
    <main className="login-page">
      <section className="login-card">
        <p className="eyebrow">BigInt Project Cloud</p>
        <h1>登入後繼續管理專案</h1>
        <p>專案內容會以版本保存，分享與變更均依 Owner／Editor／Viewer 權限處理。</p>
        {error !== undefined && <ErrorMessage message={error} />}
        <button
          type="button"
          onClick={() => window.location.assign(api.loginUrl(`${window.location.pathname}${window.location.search}`))}
        >
          使用受信任帳號登入
        </button>
        <button type="button" className="secondary" onClick={onLocal}>
          不登入，使用本機模式
        </button>
      </section>
    </main>
  );
}

function sessionUser(response: MeResponse): CurrentUser {
  return response.user;
}

export function App({ api: apiProp, editorHost: EditorHost = CloudEditorHost }: AppProps): ReactElement {
  const api = useMemo(() => apiProp ?? createCloudApiClient(), [apiProp]);
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  useEffect(() => {
    const popState = (): void => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', popState);
    return () => window.removeEventListener('popstate', popState);
  }, []);

  useEffect(() => {
    if (route.kind === 'local') return undefined;
    let cancelled = false;
    setSessionLoading(true);
    setSessionError(null);
    void api.getMe().then(
      (response) => {
        if (!cancelled) setUser(sessionUser(response));
      },
      (reason: unknown) => {
        if (cancelled) return;
        if (reason instanceof ApiError && reason.status === 401) {
          setUser(null);
          return;
        }
        setSessionError(errorMessage(reason));
      },
    ).finally(() => {
      if (!cancelled) setSessionLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, route.kind]);

  const navigate = (path: string): void => {
    window.history.pushState(null, '', path);
    setRoute(parseRoute(path));
  };
  const openProject = (id: string): void => navigate(`/projects/${encodeURIComponent(id)}`);
  const loggedOut = (): void => {
    setUser(null);
    navigate('/projects');
  };

  if (route.kind === 'local') return <LocalProjectApp onCloud={() => navigate('/projects')} />;
  if (sessionLoading) return <Loading>正在確認登入狀態…</Loading>;
  if (user === null) {
    return <LoginPage api={api} error={sessionError ?? undefined} onLocal={() => navigate('/local')} />;
  }
  if (sessionError !== null) {
    return (
      <main className="centered-card">
        <ErrorMessage message={sessionError} />
        <button type="button" onClick={() => window.location.reload()}>重試</button>
      </main>
    );
  }

  if (route.kind === 'project') {
    return (
      <ProjectWorkspace
        key={route.projectId}
        api={api}
        projectId={route.projectId}
        currentUserId={user.id}
        EditorHost={EditorHost}
        onBack={() => navigate('/projects')}
        onOpenProject={openProject}
        onLoggedOut={loggedOut}
      />
    );
  }
  if (route.kind === 'invitation') {
    return (
      <InvitationAccept
        api={api}
        token={route.token}
        onAccepted={openProject}
        onBack={() => navigate('/projects')}
      />
    );
  }
  return (
    <ProjectDashboard
      api={api}
      user={user}
      onOpenProject={openProject}
      onOpenLocal={() => navigate('/local')}
      onLoggedOut={loggedOut}
    />
  );
}
